// Arbre descendant complet en SVG natif (vectoriel → export PDF net + texte
// sélectionnable). Réutilise buildTree (qui gère déjà la fusion des couples et
// la déduplication des enfants sur tout le forest) puis pose une mise en page
// « tidy tree » descendante (générations = lignes). forwardRef sur le <svg>.
//
// Portée :
//   - rootId === null  → TOUTE la famille (tous les troncs racines).
//   - rootId === id    → uniquement les descendants de cette personne (+ couples).

import React, { forwardRef } from 'react';
import { Membre, Relation, TreeNode } from '../../types';
import { buildTree } from '../../utils/buildTree';
import { descendantSubset } from '../../utils/lineage';
import { shortDate } from '../../utils/treeVisuals';
import { StylePreset, resolveBg, PersonBox } from './chartCommon';

export interface DescendantTreeChartProps {
  rootId: number | null;              // null = toute la famille
  membres: Membre[];
  relations: Relation[];
  style: StylePreset;
  isDark: boolean;
  maxGen: number;
}

const BOX_W = 140, BOX_H = 48, COUPLE_GAP = 14, SIB_GAP = 26, ROW_GAP = 54, PAD = 26, ROOT_GAP = 60;

interface Placed { tn: TreeNode; d: number; x: number; y: number; }

const isCouple = (tn: TreeNode) => !!tn.attributes?.isCouple;
const unitW = (tn: TreeNode) => (isCouple(tn) ? 2 * BOX_W + COUPLE_GAP : BOX_W);

const DescendantTreeChart = forwardRef<SVGSVGElement, DescendantTreeChartProps>(function DescendantTreeChart(
  { rootId, membres, relations, style, isDark, maxGen }, ref,
) {
  // Forest de TreeNode : soit toute la famille, soit le sous-arbre d'une personne.
  const roots: TreeNode[] = React.useMemo(() => {
    if (rootId == null) return buildTree(membres, relations);
    const sub = descendantSubset(rootId, membres, relations);
    return buildTree(sub.membres, sub.relations);
  }, [rootId, membres, relations]);

  // Résolution TreeNode (personne) → Membre pour le rendu des boîtes.
  const map = React.useMemo(() => new Map(membres.map(m => [m.id, m])), [membres]);
  const membreOf = (tn?: TreeNode | null): Membre | undefined => {
    if (!tn) return undefined;
    const id = tn.attributes?.membreId ?? Number(tn.id);
    return map.get(id);
  };

  const childrenOf = (tn: TreeNode, d: number): TreeNode[] => (d < maxGen ? (tn.children ?? []) : []);

  // Largeur du sous-arbre (max entre l'unité et l'empilement des enfants).
  const subtreeW = (tn: TreeNode, d: number): number => {
    const kids = childrenOf(tn, d);
    let w = unitW(tn);
    if (kids.length) {
      const cw = kids.reduce((s, c) => s + subtreeW(c, d + 1), 0) + SIB_GAP * (kids.length - 1);
      w = Math.max(w, cw);
    }
    return w;
  };

  const placed: Placed[] = [];
  const edges: React.ReactNode[] = [];
  let maxRight = 0, maxBottom = 0, ei = 0;

  const place = (tn: TreeNode, d: number, leftX: number) => {
    const sw = subtreeW(tn, d);
    const uW = unitW(tn);
    const center = leftX + sw / 2;
    const x = center - uW / 2;
    const y = PAD + d * (BOX_H + ROW_GAP);
    placed.push({ tn, d, x, y });
    maxRight = Math.max(maxRight, x + uW);
    maxBottom = Math.max(maxBottom, y + BOX_H);

    const kids = childrenOf(tn, d);
    if (kids.length) {
      const cw = kids.reduce((s, c) => s + subtreeW(c, d + 1), 0) + SIB_GAP * (kids.length - 1);
      let cx = center - cw / 2;
      kids.forEach(c => {
        const csw = subtreeW(c, d + 1);
        const cCenter = cx + csw / 2;
        const y1 = y + BOX_H, y2 = y + BOX_H + ROW_GAP, midY = (y1 + y2) / 2;
        edges.push(
          <path key={`e${ei++}`}
            d={`M ${center} ${y1} L ${center} ${midY} L ${cCenter} ${midY} L ${cCenter} ${y2}`}
            fill="none" stroke={style.edge} strokeWidth={style.edgeW} strokeLinecap="round" />,
        );
        place(c, d + 1, cx);
        cx += csw + SIB_GAP;
      });
    }
  };

  let cursor = PAD;
  roots.forEach(r => { place(r, 0, cursor); cursor += subtreeW(r, 0) + ROOT_GAP; });

  const W = Math.max(Math.ceil(maxRight + PAD), 1);
  const H = Math.max(Math.ceil(maxBottom + PAD), 1);

  const boxes = placed.map((p, i) => {
    if (isCouple(p.tn)) {
      const m1 = membreOf(p.tn.attributes?.person1);
      const m2 = membreOf(p.tn.attributes?.person2);
      const leftX = p.x, rightX = p.x + BOX_W + COUPLE_GAP, midY = p.y + BOX_H / 2;
      const mDate = shortDate(p.tn.attributes?.marriageDate);
      return (
        <g key={`c${i}`}>
          <line x1={leftX + BOX_W} y1={midY} x2={rightX} y2={midY} stroke={style.edge} strokeWidth={style.edgeW} />
          {m1 && <PersonBox x={leftX} y={p.y} w={BOX_W} h={BOX_H} membre={m1} gen={p.d} style={style}
            emphasize={rootId != null && m1.id === rootId} />}
          {m2 && <PersonBox x={rightX} y={p.y} w={BOX_W} h={BOX_H} membre={m2} gen={p.d} style={style}
            emphasize={rootId != null && m2.id === rootId} />}
          {mDate && (
            <text x={(leftX + BOX_W + rightX) / 2} y={p.y - 3} fill={style.edge} fontFamily={style.font}
              fontSize={9} textAnchor="middle">{mDate}</text>
          )}
        </g>
      );
    }
    const m = membreOf(p.tn);
    if (!m) return null;
    return (
      <PersonBox key={`b${i}`} x={p.x} y={p.y} w={BOX_W} h={BOX_H} membre={m} gen={p.d} style={style}
        emphasize={rootId != null && m.id === rootId} />
    );
  });

  const bg = resolveBg(style, isDark);
  if (!placed.length) {
    return (
      <svg ref={ref} width={200} height={80} viewBox="0 0 200 80" xmlns="http://www.w3.org/2000/svg">
        <rect x={0} y={0} width={200} height={80} fill={bg} />
        <text x={100} y={44} textAnchor="middle" fontFamily={style.font} fontSize={13} fill={style.boxText === '#FFFFFF' ? '#888' : style.boxText}>Aucun membre</text>
      </svg>
    );
  }

  return (
    <svg ref={ref} width={W} height={H} viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Arbre descendant">
      <rect x={0} y={0} width={W} height={H} fill={bg} />
      {edges}
      {boxes}
    </svg>
  );
});

export default DescendantTreeChart;
